import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { User, Mail, Shield, Calendar, Zap, Save, Loader2, AlertCircle, CheckCircle2, KeyRound, Receipt, RefreshCw, XCircle, Briefcase, Clock, CreditCard, Trash2, ShieldAlert } from "lucide-react";
import AvatarUploader from "@/components/AvatarUploader";
import CertifiedBadge from "@/components/CertifiedBadge";
import GoldBadge from "@/components/GoldBadge";
import MessageInbox from "@/components/MessageInbox";
import OnlineCounter from "@/components/OnlineCounter";
import PublicProfileEditor from "@/components/PublicProfileEditor";
import SupportTigik from "@/components/SupportTigik";
import SosCenter from "@/components/SosCenter";
import PushPlayStoreNotice from "@/components/PushPlayStoreNotice";
import RefundButton from "@/components/RefundButton";
import MyCertificatesGallery from "@/components/MyCertificatesGallery";
import PlanExpiryBanner from "@/components/PlanExpiryBanner";
import NameSuggestions from "@/components/NameSuggestions";

const JOBS_TR = {
  sk: { heading: "Moje inzeráty", empty: "Zatiaľ žiadne odoslané inzeráty.", payNow: "Zaplatiť €3 a zverejniť",
        review: "Čaká na schválenie", approved: "Schválený · čaká na platbu", active: "Aktívny",
        expired: "Vypršaný" },
  en: { heading: "My ads", empty: "No submitted ads yet.", payNow: "Pay €3 and publish",
        review: "Pending review", approved: "Approved · awaiting payment", active: "Active",
        expired: "Expired" },
  cs: { heading: "Moje inzeráty", empty: "Zatím žádné odeslané inzeráty.", payNow: "Zaplatit €3 a zveřejnit",
        review: "Čeká na schválení", approved: "Schválen · čeká na platbu", active: "Aktivní",
        expired: "Vypršel" },
  de: { heading: "Meine Anzeigen", empty: "Noch keine eingereichten Anzeigen.", payNow: "€3 zahlen und veröffentlichen",
        review: "Wartet auf Freigabe", approved: "Freigegeben · wartet auf Zahlung", active: "Aktiv",
        expired: "Abgelaufen" },
  pl: { heading: "Moje ogłoszenia", empty: "Brak wysłanych ogłoszeń.", payNow: "Zapłać €3 i opublikuj",
        review: "Oczekuje na akceptację", approved: "Zaakceptowane · oczekuje na płatność", active: "Aktywne",
        expired: "Wygasło" },
  fr: {
  heading: "Mes annonces",
  empty: "Aucune annonce envoyée pour l'instant.",
  payNow: "Payer 3 € et publier",
  review: "En attente d'approbation",
  approved: "Approuvée · en attente de paiement",
  active: "Active",
  expired: "Expirée"
},
};

export default function Profile() {
  const { user, loading, refresh } = useAuth();
  const { t, lang } = useLang();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);
  // Structured error object from backend when the chosen display name collides
  // with another user — carries { message, suggestions[], code:"NAME_TAKEN" }.
  const [nameError, setNameError] = useState(null);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [subInfo, setSubInfo] = useState(null);
  const [subBusy, setSubBusy] = useState(false);

  // sync local name when the user context updates (after refresh / login)
  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  useEffect(() => {
    if (!user) return;
    api.get("/subscription/status").then((r) => setSubInfo(r.data)).catch(() => {});
  }, [user?.id]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const until = user.subscription_until ? new Date(user.subscription_until).toLocaleDateString() : null;

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true); setNameMsg(null); setNameError(null);
    try {
      await api.patch("/auth/me", { name });
      await refresh();
      setNameMsg({ kind: "ok", text: lang === "sk" ? "Uložené" : "Saved" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured NAME_TAKEN error → render suggestion pills instead of a plain toast
      if (detail && typeof detail === "object" && detail.code === "NAME_TAKEN") {
        setNameError(detail);
      } else {
        setNameMsg({ kind: "err", text: typeof detail === "string" ? detail : (detail?.message || "Error") });
      }
    } finally { setSavingName(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwBusy(true); setPwMsg(null);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      setOldPw(""); setNewPw("");
      setPwMsg({ kind: "ok", text: lang === "sk" ? "Heslo zmenené" : "Password changed" });
    } catch (err) {
      setPwMsg({ kind: "err", text: err.response?.data?.detail || "Error" });
    } finally { setPwBusy(false); }
  };

  return (
    <div data-testid="profile-page" className="max-w-3xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <span className="wb-tag">{t.nav.profile}</span>
        {user.role === "admin" && <OnlineCounter mode="registered" />}
      </div>
      <h1 className="font-display font-black text-4xl mt-2 mb-2 flex items-center gap-3 flex-wrap">
        {user.name}
        {user.plan_tier === "gold" && <GoldBadge size="md" />}
        {user.certified && <CertifiedBadge size="sm" showLabel certificateNo={user.certificate_no} />}
      </h1>

      {user.certified && (
        <div className="mb-6">
          <CertifiedBadge size="lg" certificateNo={user.certificate_no} />
        </div>
      )}

      <div className="mb-8 border border-zinc-800 bg-[var(--wb-surface)] p-5">
        <AvatarUploader />
      </div>

      {/* Schránka správ — hore aby si nemusel hľadať keď príde nová DM */}
      <MessageInbox />

      {/* Verejný profil — vidia ostatní */}
      <PublicProfileEditor user={user} onSaved={refresh} />

      {/* Podpor Tigik — donations */}
      <PushPlayStoreNotice />

      {/* 14-day money-back guarantee — appears only if eligible */}
      <RefundButton />

      <SosCenter />

      <SupportTigik />

      <div className="mb-6">
        <Link
          to={`/u/${user.id}`}
          data-testid="view-own-public-profile"
          className="wb-btn-ghost px-5 py-2 text-xs inline-flex items-center gap-2"
        >
          Pozri svoj verejný profil (ako ho vidia ostatní)
        </Link>
        {/* Internal push diagnostics — only admin/moderator/test accounts see this link */}
        {(() => {
          const role = (user.role || "").toLowerCase();
          const em = (user.email || "").toLowerCase();
          const nm = (user.name || "").toLowerCase();
          const allow = role === "admin" || role === "moderator" ||
            ["dragan", "veducko", "admin"].some((k) => em.includes(k) || nm.includes(k));
          return allow ? (
            <Link
              to="/push-debug"
              data-testid="push-debug-link"
              className="ml-2 wb-btn-ghost px-5 py-2 text-xs inline-flex items-center gap-2 text-zinc-400"
            >
              🔔 Push diagnostika
            </Link>
          ) : null;
        })()}
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-zinc-800 mb-10">
        <Cell icon={Mail} label="Email" value={user.email} testid="profile-email" />
        <Cell icon={Shield} label="Role" value={user.role || "user"} testid="profile-role" />
        <Cell icon={Zap} label={t.pricing.currentPlan} value={user.is_subscribed ? (user.plan_tier || "pro").toUpperCase() : "FREE"} accent={user.is_subscribed} testid="profile-plan" />
        {user.is_subscribed && <Cell icon={Calendar} label={t.pricing.until} value={until} testid="profile-until" />}
      </div>

      {!user.is_subscribed && (
        <Link to="/pricing" data-testid="upgrade-cta" className="wb-btn-primary inline-block px-8 py-3 text-sm mb-12">
          {lang === "sk" ? "Aktivovať Pro" : "Activate Pro"}
        </Link>
      )}

      {/* Expiry warning banner — visible only when subscription runs out within 7 days */}
      <PlanExpiryBanner user={user} lang={lang} subInfo={subInfo} />

      {/* My certificates gallery — let user add multiple cert photos (EUIPO, ISO, ...) */}
      <MyCertificatesGallery user={user} />

      {/* Subscription management (recurring plans only) */}
      {subInfo?.is_recurring && (
        <div className="border border-zinc-800 bg-[var(--wb-surface)] p-6 mb-6" data-testid="subscription-card">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <RefreshCw className={`w-5 h-5 ${subInfo.cancel_at_period_end ? "text-amber-500" : "text-green-500"}`} />
              <div>
                <div className="font-display font-bold text-base">
                  {t.pricing.paid} · {(user.plan_tier || "").toUpperCase()}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {subInfo.cancel_at_period_end ? (
                    <span className="text-amber-400">
                      Zrušené — prístup do {new Date(subInfo.subscription_until).toLocaleDateString()}, potom sa NEOBNOVÍ.
                    </span>
                  ) : (
                    <span className="text-green-500">
                      Aktívne · ďalšia obnova: {new Date(subInfo.subscription_until).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <span className={`wb-tag ${subInfo.cancel_at_period_end ? "!text-amber-400 !border-amber-700" : "!text-green-400 !border-green-700"}`}>
              {subInfo.cancel_at_period_end ? "ZRUŠENÉ" : "AUTO-OBNOVA"}
            </span>
          </div>
          {subInfo.cancel_at_period_end ? (
            <button
              data-testid="resume-subscription-btn"
              disabled={subBusy}
              onClick={async () => {
                setSubBusy(true);
                try {
                  await api.post("/subscription/resume");
                  const r = await api.get("/subscription/status");
                  setSubInfo(r.data);
                } catch (e) {
                  alert(e.response?.data?.detail || "Chyba");
                } finally { setSubBusy(false); }
              }}
              className="wb-btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
            >
              {subBusy && <Loader2 className="w-3 h-3 animate-spin" />} Obnoviť automatické obnovovanie
            </button>
          ) : (
            <button
              data-testid="cancel-subscription-btn"
              disabled={subBusy}
              onClick={async () => {
                if (!confirm("Naozaj zrušiť automatické obnovovanie? Prístup ti zostane do konca aktuálneho obdobia.")) return;
                setSubBusy(true);
                try {
                  await api.post("/subscription/cancel");
                  const r = await api.get("/subscription/status");
                  setSubInfo(r.data);
                } catch (e) {
                  alert(e.response?.data?.detail || "Chyba");
                } finally { setSubBusy(false); }
              }}
              className="wb-btn-ghost px-4 py-2 text-xs inline-flex items-center gap-2 !text-red-400 !border-red-800"
            >
              {subBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
              Zrušiť automatickú obnovu
            </button>
          )}
          <div className="text-[10px] font-mono text-zinc-600 mt-3 leading-relaxed">
            Pri zrušení si zachováš prístup až do konca zaplateného obdobia. Žiadne ďalšie strhávanie.
          </div>
        </div>
      )}

      {/* Billing details */}
      <div className="border border-zinc-800 bg-[var(--wb-surface)] p-6 mb-6 flex items-center justify-between gap-4" data-testid="billing-card">
        <div className="flex items-center gap-3">
          <Receipt className="w-5 h-5 text-gold-500" />
          <div>
            <div className="font-display font-bold text-base">Fakturačné údaje</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {user.billing_filled
                ? <span className="text-green-500">✓ Vyplnené — {user.billing?.legal_name}</span>
                : <span className="text-amber-400">⚠ Neúplné — potrebné pre platby</span>}
            </div>
          </div>
        </div>
        <Link to="/profile/billing" data-testid="billing-edit-link"
          className={user.billing_filled ? "wb-btn-ghost px-4 py-2 text-xs" : "wb-btn-primary px-4 py-2 text-xs"}>
          {user.billing_filled ? t.profile.billingEdit : t.profile.billingFill}
        </Link>
      </div>

      <MyJobsSection lang={lang} />

      {/* Change name */}
      <div className="border border-zinc-800 bg-[var(--wb-surface)] p-6 mb-6" data-testid="change-name-card">
        <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-gold-500" />
          {lang === "sk" ? "Zmena mena" : "Change name"}
        </h2>
        <form onSubmit={saveName} className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="change-name-input"
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            required minLength={1} maxLength={80}
            className="wb-input flex-1 px-3 py-2 text-sm"
          />
          <button data-testid="change-name-save" type="submit" disabled={savingName}
            className="wb-btn-primary px-5 py-2 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {lang === "sk" ? "Uložiť" : "Save"}
          </button>
        </form>
        {nameMsg && <Msg msg={nameMsg} testid="change-name-msg" />}
        <NameSuggestions
          error={nameError}
          lang={lang}
          onPick={(s) => { setName(s); setNameError(null); }}
          testid="change-name-suggestions"
        />
      </div>

      {/* Change password (password auth only) */}
      {user.auth_provider !== "google" && (
      <div className="border border-zinc-800 bg-[var(--wb-surface)] p-6" data-testid="change-pw-card">
        <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-gold-500" />
          {lang === "sk" ? "Zmena hesla" : "Change password"}
        </h2>
        <form onSubmit={changePw} className="space-y-3">
          <input
            data-testid="change-pw-old"
            type="password" required placeholder={lang === "sk" ? "Aktuálne heslo" : "Current password"}
            value={oldPw} onChange={(e) => setOldPw(e.target.value)}
            className="wb-input w-full px-3 py-2 text-sm"
          />
          <input
            data-testid="change-pw-new"
            type="password" required minLength={6} placeholder={lang === "sk" ? "Nové heslo (min. 6 znakov)" : "New password (min. 6 chars)"}
            value={newPw} onChange={(e) => setNewPw(e.target.value)}
            className="wb-input w-full px-3 py-2 text-sm"
          />
          <button data-testid="change-pw-save" type="submit" disabled={pwBusy}
            className="wb-btn-primary px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {lang === "sk" ? "Zmeniť heslo" : "Change password"}
          </button>
        </form>
        {pwMsg && <Msg msg={pwMsg} testid="change-pw-msg" />}
      </div>
      )}

      {/* Danger Zone — GDPR right to erasure (Iteration 73) */}
      <DangerZone user={user} lang={lang} />
    </div>
  );
}

function DangerZone({ user, lang }) {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isGoogle = user?.auth_provider === "google";
  const isAdmin = user?.role === "admin";

  const TR = {
    sk: {
      title: "Nebezpečná zóna",
      warn: "Ak zmažeš svoj účet, stratíš prístup k všetkému. Táto akcia je nezvratná.",
      btn: "Zmazať môj účet",
      modalTitle: "Nenávratne zmazať účet?",
      modalWarn: "Zmažú sa tvoje osobné dáta: profil, správy v komunite (anonymizované), inzeráty, DM, notifikácie, HoF záznam. Ostanú iba faktúry (10-ročná archivácia podľa zákona o účtovníctve, bez tvojich osobných údajov).",
      confirmLabel: "Napíš",
      confirmPhrase: "ZMAZAT",
      confirmSuffix: "pre potvrdenie:",
      passwordLabel: "Zadaj heslo:",
      cancel: "Zrušiť",
      confirm: "Nenávratne zmazať",
      adminBlock: "Admin účty nemôžu byť takto zmazané. Kontaktuj iného admina.",
    },
    en: {
      title: "Danger zone", warn: "Deleting your account is permanent. All personal data will be erased.",
      btn: "Delete my account", modalTitle: "Permanently delete your account?",
      modalWarn: "Your personal data will be erased: profile, community messages (anonymized), ads, DMs, notifications, HoF record. Only invoices are kept (10-year retention per accounting law, without your personal data).",
      confirmLabel: "Type", confirmPhrase: "DELETE", confirmSuffix: "to confirm:",
      passwordLabel: "Enter password:", cancel: "Cancel", confirm: "Permanently delete",
      adminBlock: "Admin accounts cannot self-delete. Ask another admin.",
    },
    cs: {
      title: "Nebezpečná zóna", warn: "Pokud si smažeš účet, ztratíš přístup ke všemu. Akce je nevratná.",
      btn: "Smazat můj účet", modalTitle: "Nenávratně smazat účet?",
      modalWarn: "Smažou se tvá osobní data: profil, zprávy v komunitě (anonymizované), inzeráty, DM, notifikace, HoF záznam. Zůstanou pouze faktury (10-letá archivace dle zákona o účetnictví, bez osobních údajů).",
      confirmLabel: "Napiš", confirmPhrase: "SMAZAT", confirmSuffix: "pro potvrzení:",
      passwordLabel: "Zadej heslo:", cancel: "Zrušit", confirm: "Nenávratně smazat",
      adminBlock: "Admin účty nelze takto smazat. Kontaktuj jiného admina.",
    },
    de: {
      title: "Gefahrenzone", warn: "Das Löschen deines Kontos ist endgültig. Alle persönlichen Daten werden entfernt.",
      btn: "Konto löschen", modalTitle: "Konto endgültig löschen?",
      modalWarn: "Deine persönlichen Daten werden gelöscht: Profil, Community-Nachrichten (anonymisiert), Anzeigen, DMs, Benachrichtigungen, HoF-Eintrag. Nur Rechnungen bleiben erhalten (10-jährige Aufbewahrung nach Buchhaltungsgesetz, ohne persönliche Daten).",
      confirmLabel: "Tippe", confirmPhrase: "LÖSCHEN", confirmSuffix: "zur Bestätigung:",
      passwordLabel: "Passwort eingeben:", cancel: "Abbrechen", confirm: "Endgültig löschen",
      adminBlock: "Admin-Konten können sich nicht selbst löschen. Frage einen anderen Admin.",
    },
    pl: {
      title: "Strefa niebezpieczna", warn: "Usunięcie konta jest nieodwracalne. Wszystkie dane osobowe zostaną usunięte.",
      btn: "Usuń moje konto", modalTitle: "Nieodwracalnie usunąć konto?",
      modalWarn: "Twoje dane osobowe zostaną usunięte: profil, wiadomości w społeczności (zanonimizowane), ogłoszenia, DM, powiadomienia, wpis w HoF. Pozostaną tylko faktury (10-letnia archiwizacja zgodnie z ustawą o rachunkowości, bez danych osobowych).",
      confirmLabel: "Napisz", confirmPhrase: "USUŃ", confirmSuffix: "aby potwierdzić:",
      passwordLabel: "Podaj hasło:", cancel: "Anuluj", confirm: "Nieodwracalnie usuń",
      adminBlock: "Konta admin nie mogą się same usunąć. Skontaktuj się z innym adminem.",
    },
    fr: {
  title: "Zone dangereuse",
  warn: "Si tu supprimes ton compte, tu perdras l'accès à tout. Cette action est irréversible.",
  btn: "Supprimer mon compte",
  modalTitle: "Supprimer définitivement le compte ?",
  modalWarn: "Tes données personnelles seront supprimées : profil, messages communautaires (anonymisés), annonces, DM, notifications, entrée Hall of Fame. Seules les factures resteront (archivage 10 ans selon la loi comptable, sans tes données personnelles).",
  confirmLabel: "Tape",
  confirmPhrase: "SUPPRIMER",
  confirmSuffix: "pour confirmer :",
  passwordLabel: "Saisis ton mot de passe :",
  cancel: "Annuler",
  confirm: "Supprimer définitivement",
  adminBlock: "Les comptes admin ne peuvent pas être supprimés ainsi. Contacte un autre admin.",
},
  };
  const t = TR[lang] || TR.sk;

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.delete("/auth/me", {
        data: {
          password: isGoogle ? undefined : password,
          confirm_phrase: confirmText.trim(),
        },
      });
      logout();
      window.location.href = "/?deleted=1";
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const canConfirm = confirmText.trim().toUpperCase() === t.confirmPhrase && (isGoogle || password.length > 0);

  return (
    <>
      <div className="border border-red-900/50 bg-red-950/10 p-6" data-testid="danger-zone-card">
        <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2 text-red-400">
          <ShieldAlert className="w-4 h-4" />
          {t.title}
        </h2>
        <p className="text-xs text-zinc-400 mb-4">{t.warn}</p>
        {isAdmin ? (
          <p className="text-xs text-yellow-500/80">{t.adminBlock}</p>
        ) : (
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-red-800 text-red-400 hover:bg-red-950/40 hover:border-red-600 text-xs font-mono uppercase tracking-widest transition-colors"
            data-testid="danger-delete-account-btn"
          >
            <Trash2 className="w-3 h-3" /> {t.btn}
          </button>
        )}
      </div>

      {/* Confirmation modal */}
      {open && !isAdmin && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }}
          data-testid="danger-delete-modal"
        >
          <div className="w-full max-w-md bg-black border border-red-800 p-6">
            <h3 className="font-display font-bold text-lg text-red-400 mb-3 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> {t.modalTitle}
            </h3>
            <p className="text-sm text-zinc-400 mb-4 leading-relaxed">{t.modalWarn}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
                  {t.confirmLabel} <span className="text-red-400 font-bold">{t.confirmPhrase}</span> {t.confirmSuffix}
                </label>
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="wb-input w-full px-3 py-2 text-sm font-mono"
                  data-testid="danger-confirm-phrase"
                  autoFocus
                />
              </div>
              {!isGoogle && (
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.passwordLabel}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="wb-input w-full px-3 py-2 text-sm"
                    data-testid="danger-confirm-password"
                  />
                </div>
              )}
              {err && (
                <div className="p-2 border border-red-900 bg-red-950/30 text-red-400 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> {err}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setOpen(false)}
                  disabled={busy}
                  className="flex-1 px-4 py-2 border border-zinc-700 text-zinc-300 hover:border-zinc-500 text-xs font-mono uppercase tracking-widest disabled:opacity-50"
                  data-testid="danger-cancel-btn"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={submit}
                  disabled={!canConfirm || busy}
                  className="flex-1 px-4 py-2 border border-red-700 bg-red-950/40 text-red-300 hover:bg-red-900/40 hover:border-red-500 text-xs font-mono uppercase tracking-widest disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="danger-confirm-btn"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  {t.confirm}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Msg({ msg, testid }) {
  const ok = msg.kind === "ok";
  const Icon = ok ? CheckCircle2 : AlertCircle;
  return (
    <div data-testid={testid}
      className={`mt-3 flex items-center gap-2 p-2 text-sm border ${ok ? "border-green-900 bg-green-950/30 text-green-400" : "border-red-900 bg-red-950/30 text-red-400"}`}>
      <Icon className="w-4 h-4" /> {msg.text}
    </div>
  );
}

function Cell({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-[var(--wb-surface)] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-base font-medium ${accent ? "text-gold-500" : "text-white"}`}>{value}</div>
    </div>
  );
}

function MyJobsSection({ lang }) {
  const tr = JOBS_TR[lang] || JOBS_TR.sk;
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/me/jobs").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const pay = async (adId) => {
    setBusy(adId);
    try {
      const r = await api.post(`/jobs/${adId}/checkout`, {});
      window.location.href = r.data.url;
    } catch (e) {
      const detail = e.response?.data?.detail || "Error";
      if (typeof detail === "string" && detail.startsWith("BILLING_REQUIRED")) {
        window.location.href = `/profile/billing?first=1&next=${encodeURIComponent("/profile")}`;
        return;
      }
      alert(detail);
      setBusy(null);
    }
  };

  if (items === null) return null;
  if (items.length === 0) return null; // hide entirely if no ads — keep profile clean

  const STATUS = {
    pending_review: { cls: "!text-amber-400 !border-amber-700", label: tr.review, icon: Clock },
    pending_payment: { cls: "!text-green-400 !border-green-700", label: tr.approved, icon: CreditCard },
    active: { cls: "!text-blue-400 !border-blue-700", label: tr.active, icon: CheckCircle2 },
    expired: { cls: "!text-zinc-500 !border-zinc-700", label: tr.expired, icon: XCircle },
  };

  return (
    <div className="border border-zinc-800 bg-[var(--wb-surface)] p-6 mb-6" data-testid="my-jobs-card">
      <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-gold-500" />
        {tr.heading}
        <span className="text-xs font-mono text-zinc-500">({items.length})</span>
      </h2>
      <div className="space-y-2">
        {items.map((j) => {
          const meta = STATUS[j.status] || { cls: "", label: j.status, icon: Briefcase };
          const StatusIcon = meta.icon;
          return (
            <div key={j.id} data-testid={`my-job-${j.id}`}
              className="flex items-center justify-between gap-3 p-3 border border-zinc-800 hover:border-gold-700/40 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm truncate">{j.title}</div>
                <div className="text-xs text-zinc-500 truncate">{j.company} · {j.location}</div>
              </div>
              <span className={`wb-tag inline-flex items-center gap-1 ${meta.cls}`}>
                <StatusIcon className="w-3 h-3" /> {meta.label}
              </span>
              {j.status === "pending_payment" && (
                <button onClick={() => pay(j.id)} disabled={busy === j.id}
                  data-testid={`my-job-${j.id}-pay`}
                  className="wb-btn-primary px-3 py-2 text-xs inline-flex items-center gap-1.5 whitespace-nowrap">
                  {busy === j.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                  {tr.payNow}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
